import React, { useMemo, useState } from 'react';
import {
    Box,
    Modal,
    Fade,
    Backdrop,
    Typography,
    Checkbox,
    Button,
    IconButton,
    Divider,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { replacePlaceholders } from '../../utils/helper';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ConsentPara {
    title?: string;
    content: string;
    placeholders?: Record<string, string>;
}

interface ConsentModalProps {
    open: boolean;
    paras: ConsentPara[];
    /** Fires when the user accepts the terms and clicks Proceed. */
    onProceed: () => void;
    /** Fires when the user dismisses the modal (X / backdrop / Esc) → caller logs out. */
    onClose: () => void;
    /** Shows a spinner on the Proceed CTA while post-consent work is in flight. */
    submitting?: boolean;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    paper: {
        position: 'relative',
        width: '92%',
        maxWidth: 460,
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(38, 50, 56, 0.24)',
        outline: 'none',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
    },
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: '#263238',
    },
    closeBtn: {
        color: '#90a4ae',
    },
    body: {
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: '4px 24px 8px',
    },
    paraTitle: {
        fontSize: 15,
        fontWeight: 700,
        color: '#263238',
        marginTop: 18,
        marginBottom: 6,
    },
    paraContent: {
        fontSize: 14,
        lineHeight: 1.7,
        color: '#546e7a',
        textAlign: 'justify',
    },
    footer: {
        padding: '12px 24px 24px',
    },
    ackRow: {
        display: 'flex',
        alignItems: 'flex-start',
        cursor: 'pointer',
        marginBottom: 16,
        userSelect: 'none',
    },
    checkbox: {
        padding: 0,
        marginTop: 2,
        marginRight: 12,
        color: '#b0bec5',
        '&.Mui-checked': { color: '#43419e' },
    },
    ackLabel: {
        fontSize: 14,
        fontWeight: 600,
        color: '#37474f',
        lineHeight: 1.5,
    },
    proceedBtn: {
        height: 48,
        fontSize: 16,
        fontWeight: 700,
        textTransform: 'none',
        backgroundColor: '#43419e',
        color: '#fff',
        '&:hover': { backgroundColor: '#37357f' },
        '&.Mui-disabled': {
            backgroundColor: '#c5cae9',
            color: '#fff',
        },
    },
}));

// ─── Component ────────────────────────────────────────────────────────────────

const ConsentModal: React.FC<ConsentModalProps> = ({
    open,
    paras,
    onProceed,
    onClose,
    submitting = false,
}) => {
    const classes = useStyles();
    const [acknowledged, setAcknowledged] = useState(false);

    // Pre-interpolate each paragraph's placeholders once per content change.
    const renderedParas = useMemo(
        () =>
            (paras ?? []).map((para) => ({
                title: para.title,
                content: replacePlaceholders(para.content, para.placeholders),
            })),
        [paras],
    );

    // Block dismissal while the post-consent flow is running to avoid a
    // half-completed login being torn down mid-request.
    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 300 }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-labelledby="consent-modal-title"
        >
            <Fade in={open}>
                <Box className={classes.paper}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <Typography id="consent-modal-title" className={classes.title}>
                            Terms &amp; conditions
                        </Typography>
                        <IconButton
                            size="small"
                            className={classes.closeBtn}
                            onClick={handleClose}
                            disabled={submitting}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />

                    {/* Scrollable consent body */}
                    <Box className={classes.body}>
                        {renderedParas.map((para, index) => (
                            <Box key={index}>
                                {para.title && (
                                    <Typography className={classes.paraTitle}>
                                        {para.title}
                                    </Typography>
                                )}
                                <Typography className={classes.paraContent}>
                                    {para.content}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Divider />

                    {/* Footer: acknowledgement + CTA */}
                    <Box className={classes.footer}>
                        <label className={classes.ackRow}>
                            <Checkbox
                                className={classes.checkbox}
                                checked={acknowledged}
                                disabled={submitting}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                disableRipple
                                color="default"
                            />
                            <Typography className={classes.ackLabel}>
                                I acknowledge that I have read and fully understand the
                                terms and conditions of the agreement
                            </Typography>
                        </label>

                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            className={classes.proceedBtn}
                            disabled={!acknowledged || submitting}
                            onClick={onProceed}
                            startIcon={
                                submitting ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : undefined
                            }
                        >
                            {submitting ? 'Please wait…' : 'Proceed'}
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ConsentModal;
